import { getAvatarFullUrl, getFullName } from "@features/auth/utils";
import { useUser } from "@features/clients/state/use-client-users";
import { useComment } from "@features/comments/hooks/use-comments";
import { Comments } from "@features/comments/types/types";
import { PublicCustomer } from "@features/customers/types/customers";
import { useDraftRest } from "@features/utils/rest/hooks/use-draft-rest";
import { RestEntity } from "@features/utils/rest/types/types";
import {
  ArrowUturnLeftIcon,
  FaceSmileIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
} from "@heroicons/react/16/solid";
import { EditorInput } from "@molecules/editor-input";
import {
  Avatar,
  Card,
  IconButton,
  Strong,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { format, formatDistance } from "date-fns";
import { useState } from "react";
import toast from "react-hot-toast";
import { Event } from "./events";

export const CommentCard = ({
  id,
  item,
}: {
  id: string;
  item?: RestEntity & any;
  viewRoute?: string;
}) => {
  const { comment } = useComment(id);
  const { user } = useUser(comment?.created_by || "");

  if (!comment) return <></>;

  const fullName =
    getFullName(user?.user as PublicCustomer) || user?.user?.email || "Unknown";
  if (comment.type === "event" && item) {
    return <Event id={comment.id} />;
  }

  return (
    <Card className="mb-2 mt-4 space-y-2 group/comment">
      <div className="w-full flex items-center space-x-2">
        <div className="w-4 h-4 flex items-start">
          <Avatar
            variant="solid"
            style={{ backgroundColor: "#FFD700" }}
            radius="full"
            className="w-4 h-4"
            size="1"
            src={
              getAvatarFullUrl((user?.user as PublicCustomer)?.avatar) ||
              undefined
            }
            fallback={fullName[0]}
          />
        </div>
        <Text size="2" className="text-gray-500 grow">
          <Strong className="text-black dark:text-white">{fullName}</Strong>
          {" - "}
          <Tooltip
            content={format(
              new Date(parseInt(comment.created_at || "0") || 0),
              "PPpp",
            )}
          >
            <span>
              {formatDistance(new Date(comment.created_at || 0), new Date(), {
                addSuffix: true,
              })}
            </span>
          </Tooltip>
        </Text>
        {!!comment.content && false && (
          <div className="flex items-center space-x-2 group-hover/comment:opacity-100 opacity-0 transition-all">
            <Tooltip content="Ajouter une réaction">
              <IconButton variant="ghost" radius="full" size="1">
                <FaceSmileIcon className="w-4 h-4" />
              </IconButton>
            </Tooltip>
            <Tooltip content="Répondre">
              <IconButton variant="ghost" radius="full" size="1">
                <ArrowUturnLeftIcon className="w-4 h-4" />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>
      {!!comment.content && (
        <Text size="3" className="block">
          <EditorInput disabled value={comment.content} placeholder="" />
        </Text>
      )}
    </Card>
  );
};

export const CommentCreate = ({
  entity,
  item,
  refresh,
  disabled = false,
}: {
  entity: string;
  item: string;
  refresh: () => void;
  disabled?: boolean;
}) => {
  const { save } = useDraftRest<Comments>("comments", "new", async () => {});

  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [attachments, setAttachments] = useState<string[]>([]);

  return (
    <Card className="space-y-2" variant="surface">
      <EditorInput
        disabled={disabled}
        value={comment}
        onChange={setComment}
        className="border-none bg-transparent dark:bg-transparent p-0"
        placeholder="Ajouter un commentaire..."
      />
      <div className="flex items-center space-x-2">
        <div className="grow" />
        {false && (
          <Tooltip content="Pièces jointes">
            <IconButton
              variant="ghost"
              radius="full"
              loading={loading}
              disabled={loading}
            >
              <PaperClipIcon className="w-4 h-4 -rotate-90" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip content="Envoyer">
          <IconButton
            loading={loading}
            variant="solid"
            radius="full"
            disabled={disabled || loading || !comment}
            onClick={async () => {
              setLoading(true);
              try {
                const val = await save({
                  item_entity: entity,
                  item_id: item,
                  content: comment,
                  documents: attachments,
                  type: "comment",
                  reactions: [],
                });
                if (!val) throw new Error("No value returned");
                refresh();
                setComment("");
                setAttachments([]);
              } catch (e: any) {
                console.error(e);
                toast.error("Impossible d'envoyer le commentaire.");
              } finally {
                setLoading(false);
              }
            }}
          >
            <PaperAirplaneIcon className="w-4 h-4 -rotate-90" />
          </IconButton>
        </Tooltip>
      </div>
    </Card>
  );
};
